import { useMemo } from 'react';
import type { OrderDetailsRequestData, OrderPageData, PartialLabelData, SliderUIObjectData } from '../../api';
import { type PriceLine, PriceLineType, type PriceQuote, quotePrice } from '../core/pricing';
import { asOrderPageData } from '../core/types';
import { PluginType } from '../PluginUtils';
import { useOrderPage } from '../orderPageContext/useOrderPage';

/**
 * Below this the per-person rate barely moves, so "bring one more" is noise rather than news.
 * It doubles as tier detection: on a ladder that steps every value it fires only on the real steps.
 */
export const MEANINGFUL_DROP_PCT = 2;

/** Keeps a wide slider from turning one render into hundreds of quotes while hunting for a discount. */
const MAX_LADDER_SAMPLES = 60;

export type NextStep = { persons: number; perPerson: number; dropPct: number };

export type PriceExplanation = {
  /** False means render none of the discount UI — nothing is actually discounted, or this is a multi-ticket page. */
  shouldExplain: boolean;
  total: number;
  lines: PriceLine[];
  surcharge: PriceLine | null;
  persons: number;
  /** Rounded up, so the figure shown is never below the true share of the total. */
  perPerson: number;
  /** What the same party would pay at the entry-level rate, or null when there is no ladder. */
  listPrice: number | null;
  savings: number;
  nextStep: NextStep | null;
  /** Cheapest per-person rate reachable on this page — the headline anchor. */
  fromPerPerson: number | null;
  /** True when the ladder makes fromPerPerson a "from" price rather than the only price. */
  isFromPrice: boolean;
  /** Per-person rate at each end of the slider — the only two prices honest enough to put on its marks. */
  rateAtMin: number | null;
  rateAtMax: number | null;
  /** What one slider unit is called (e.g. "project") — null means the units are people. */
  unitLabel: PartialLabelData | null;
};

const EMPTY: PriceExplanation = {
  shouldExplain: false,
  total: 0,
  lines: [],
  surcharge: null,
  persons: 1,
  perPerson: 0,
  listPrice: null,
  savings: 0,
  nextStep: null,
  fromPerPerson: null,
  isFromPrice: false,
  rateAtMin: null,
  rateAtMax: null,
  unitLabel: null,
};

/** Split out from the hook so it can be unit-tested without a provider. */
export const explainPrice = ({
  orderPage,
  details,
  dateAndTimeDetails,
  account,
  personsOverride,
}: {
  orderPage: OrderPageData | undefined;
  details: OrderDetailsRequestData;
  dateAndTimeDetails: { date?: string | null } | undefined;
  account: any;
  personsOverride?: number;
}): PriceExplanation => {
  if (!orderPage) {
    return EMPTY;
  }

  const slider = orderPage.objects.find((o) => o.type === 'slider') as SliderUIObjectData | undefined;
  const isMultiTicket = orderPage.objects.some((o) => o.type === 'multipleTickets');
  const quantityKey = slider?.data_type || 'persons';
  const persons = Math.max(1, personsOverride ?? (details as Record<string, any>)[quantityKey] ?? 1);

  // Same rule the provider applies before storing the price, so these numbers match the summary's.
  const forceWeekendAddition = details.custom_details?.[PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS] === true;

  const quoteAt = (n: number): PriceQuote =>
    quotePrice({
      orderPage,
      details: { ...details, [quantityKey]: n } as OrderDetailsRequestData,
      dateDetails: dateAndTimeDetails,
      account,
      forceWeekendAddition,
    });
  const rateAt = (n: number) => quoteAt(n).total / n;
  /** The headline anchor sits above the fold, far from the date picker, so it must not move when a date is picked. */
  const datelessRateAt = (n: number) =>
    quotePrice({
      orderPage,
      details: { ...details, [quantityKey]: n } as OrderDetailsRequestData,
      dateDetails: undefined,
      account,
      forceWeekendAddition: false,
    }).total / n;

  const quote = quoteAt(persons);
  const { total } = quote;
  const surcharge = quote.lines.find((l) => l.type === PriceLineType.SURCHARGE) ?? null;

  const min = slider?.min_value ?? 1;
  const max = slider?.max_value ?? persons;
  const baseRate = rateAt(min);
  const bestRate = rateAt(max);
  // "The price is always the same" means the per-person rate is flat, not that the total is.
  const ladderPct = baseRate > 0 ? ((baseRate - bestRate) / baseRate) * 100 : 0;
  const rateFalls = !!slider && max > min && ladderPct >= MEANINGFUL_DROP_PCT;

  /**
   * A falling rate is not proof of a discount: a minimum charge divided among more people falls the
   * same way while every head still costs the same. Only a cheaper extra unit is a real discount.
   */
  const marginalEverFalls = () => {
    const step = Math.max(1, Math.ceil((max - min) / MAX_LADDER_SAMPLES));
    let previous: number | null = null;
    for (let n = min + step; n <= max; n += step) {
      const marginal = (quoteAt(n).total - quoteAt(n - step).total) / step;
      if (previous !== null && marginal < previous - 0.01) {
        return true;
      }
      previous = marginal;
    }
    return false;
  };

  const hasLadder = rateFalls && marginalEverFalls();
  const shouldExplain = !isMultiTicket && hasLadder;

  const listPrice = shouldExplain ? Math.round(baseRate * persons) : null;

  let nextStep: NextStep | null = null;
  if (shouldExplain && persons < max) {
    const nextPerPerson = rateAt(persons + 1);
    const currentPerPerson = total / persons;
    const dropPct = currentPerPerson > 0 ? ((currentPerPerson - nextPerPerson) / currentPerPerson) * 100 : 0;
    if (dropPct >= MEANINGFUL_DROP_PCT) {
      nextStep = { persons: persons + 1, perPerson: Math.ceil(nextPerPerson), dropPct };
    }
  }

  return {
    shouldExplain,
    total,
    lines: quote.lines,
    surcharge,
    persons,
    perPerson: Math.ceil(total / persons),
    listPrice,
    savings: listPrice !== null ? Math.max(0, listPrice - total) : 0,
    nextStep,
    // The anchor is worth showing even with no ladder — it just stops being a "from" price.
    // It follows the rate, not the discount: under a minimum charge the big-party rate is still
    // the cheapest anyone pays, so it anchors the page even with no discount to tell.
    fromPerPerson: isMultiTicket || !slider ? null : Math.ceil(datelessRateAt(rateFalls ? max : min)),
    isFromPrice: rateFalls,
    rateAtMin: slider ? Math.ceil(baseRate) : null,
    rateAtMax: slider ? Math.ceil(bestRate) : null,
    unitLabel: orderPage.price_unit_label ?? null,
  };
};

type Options = {
  /** The slider's uncommitted value, so the price can track a drag before the state round-trip. */
  personsOverride?: number;
};

export const usePriceExplanation = ({ personsOverride }: Options = {}): PriceExplanation => {
  const { orderPageState } = useOrderPage();
  const { details, dateAndTimeDetails, account } = orderPageState;
  const orderPage = asOrderPageData(orderPageState.orderPage);

  return useMemo(
    () => explainPrice({ orderPage, details, dateAndTimeDetails, account, personsOverride }),
    [orderPage, details, dateAndTimeDetails, account, personsOverride],
  );
};
